import { assertValidConfig, resolveGameConfig } from "./config.js";
import { deterministicJsonHash, hashGameState, stableJsonStringify } from "./hash.js";
import { ReplayDivergenceError } from "./replay.js";
import { cancelMatch, cloneGameState, forfeitMatch, pauseMatch, resumeMatch, setPlayerConnected, setPlayerReady, stepGame, } from "./simulation.js";
import { PHYSICS_VERSION, PROTOCOL_VERSION, TICK_RATE, } from "./types.js";
export const REPLAY_ARTIFACT_VERSION = "air-hockey-replay-v1";
export const DEFAULT_REPLAY_CHECKPOINT_INTERVAL_TICKS = TICK_RATE * 5;
/** One hour at 60 Hz, with deliberately separate aggregate and per-frame bounds. */
export const DEFAULT_REPLAY_ARTIFACT_LIMITS = Object.freeze({
    maxBytes: 32 * 1024 * 1024,
    maxFrames: TICK_RATE * 60 * 60,
    maxInputs: 1_000_000,
    maxInputsPerFrame: 256,
    maxControlTransitions: 100_000,
    maxImportantEvents: 100_000,
    maxCheckpoints: 10_000,
    maxStringLength: 512,
    maxCheckpointIntervalTicks: TICK_RATE * 60 * 60,
});
export class ReplayArtifactValidationError extends Error {
    path;
    constructor(path, message) {
        super(`${path}: ${message}`);
        this.name = "ReplayArtifactValidationError";
        this.path = path;
    }
}
export class ReplayArtifactVersionError extends ReplayArtifactValidationError {
    expectedVersion;
    actualVersion;
    constructor(path, expectedVersion, actualVersion) {
        super(path, `expected version ${expectedVersion}`);
        this.name = "ReplayArtifactVersionError";
        this.expectedVersion = expectedVersion;
        this.actualVersion = actualVersion;
    }
}
const HASH_PATTERN = /^[0-9a-f]{8}$/;
const UTC_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?Z$/;
const PLAYER_SLOTS = ["PLAYER_A", "PLAYER_B"];
const MATCH_MODES = ["classic", "timed", "sudden-death"];
const MATCH_PHASES = [
    "waiting",
    "ready",
    "countdown",
    "playing",
    "goal",
    "paused",
    "finished",
];
const PAUSABLE_PHASES = [
    "waiting",
    "ready",
    "countdown",
    "playing",
    "goal",
];
const FINISH_REASONS = ["score", "time", "forfeit", "cancelled"];
function fail(path, message) {
    throw new ReplayArtifactValidationError(path, message);
}
function expectRecord(value, path, requiredKeys, optionalKeys = []) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return fail(path, "expected an object");
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        return fail(path, "expected a plain object");
    }
    if (Object.getOwnPropertySymbols(value).length > 0) {
        return fail(path, "symbol keys are not allowed");
    }
    const keys = Object.getOwnPropertyNames(value);
    const allowed = new Set([...requiredKeys, ...optionalKeys]);
    for (const key of requiredKeys) {
        if (!Object.prototype.hasOwnProperty.call(value, key))
            fail(`${path}.${key}`, "missing required field");
    }
    for (const key of keys) {
        if (!allowed.has(key))
            fail(`${path}.${key}`, "unknown field");
    }
    return value;
}
function expectArray(value, path, maximum) {
    if (!Array.isArray(value))
        return fail(path, "expected an array");
    if (value.length > maximum)
        return fail(path, `exceeds maximum count ${maximum}`);
    if (Object.getOwnPropertySymbols(value).length > 0)
        fail(path, "symbol keys are not allowed");
    const keys = Object.keys(value);
    if (keys.length !== value.length || keys.some((key, index) => key !== String(index))) {
        fail(path, "sparse arrays and array properties are not allowed");
    }
    return value;
}
function expectBoolean(value, path) {
    if (typeof value !== "boolean")
        return fail(path, "expected a boolean");
    return value;
}
function expectFiniteNumber(value, path) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return fail(path, "expected a finite number");
    }
    return Object.is(value, -0) ? 0 : value;
}
function expectSafeInteger(value, path, minimum = 0) {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum) {
        return fail(path, `expected a safe integer >= ${minimum}`);
    }
    return value;
}
function expectString(value, path, limits, allowEmpty = false) {
    if (typeof value !== "string")
        return fail(path, "expected a string");
    if ((!allowEmpty && value.length === 0) || value.length > limits.maxStringLength) {
        return fail(path, `expected ${allowEmpty ? "a" : "a non-empty"} string of at most ${limits.maxStringLength} characters`);
    }
    return value;
}
function expectNullableString(value, path, limits) {
    return value === null ? null : expectString(value, path, limits);
}
function expectTimestamp(value, path, limits) {
    const timestamp = expectNullableString(value, path, limits);
    if (timestamp !== null) {
        const parts = UTC_TIMESTAMP_PATTERN.exec(timestamp);
        if (parts === null)
            fail(path, "expected a UTC RFC 3339 timestamp");
        const year = Number(parts[1]);
        const month = Number(parts[2]);
        const day = Number(parts[3]);
        const hour = Number(parts[4]);
        const minute = Number(parts[5]);
        const second = Number(parts[6]);
        const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
        const daysByMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        const days = daysByMonth[month - 1];
        if (year < 1
            || days === undefined
            || day < 1
            || day > days
            || hour > 23
            || minute > 59
            || second > 59) {
            fail(path, "expected a valid UTC RFC 3339 timestamp");
        }
    }
    return timestamp;
}
function timestampOrderKey(value) {
    const parts = UTC_TIMESTAMP_PATTERN.exec(value);
    if (parts === null)
        return value;
    return `${value.slice(0, 19)}.${(parts[7] ?? "").padEnd(9, "0")}`;
}
function expectEnum(value, path, allowed) {
    if (typeof value !== "string" || !allowed.some((entry) => entry === value)) {
        return fail(path, `expected one of ${allowed.join(", ")}`);
    }
    return value;
}
function expectNullableEnum(value, path, allowed) {
    return value === null ? null : expectEnum(value, path, allowed);
}
function expectHash(value, path) {
    if (typeof value !== "string" || !HASH_PATTERN.test(value)) {
        return fail(path, "expected an eight-character lowercase hexadecimal hash");
    }
    return value;
}
function resolveLimits(overrides = {}) {
    const limits = {
        maxBytes: overrides.maxBytes ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxBytes,
        maxFrames: overrides.maxFrames ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxFrames,
        maxInputs: overrides.maxInputs ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxInputs,
        maxInputsPerFrame: overrides.maxInputsPerFrame ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxInputsPerFrame,
        maxControlTransitions: overrides.maxControlTransitions ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxControlTransitions,
        maxImportantEvents: overrides.maxImportantEvents ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxImportantEvents,
        maxCheckpoints: overrides.maxCheckpoints ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxCheckpoints,
        maxStringLength: overrides.maxStringLength ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxStringLength,
        maxCheckpointIntervalTicks: overrides.maxCheckpointIntervalTicks ?? DEFAULT_REPLAY_ARTIFACT_LIMITS.maxCheckpointIntervalTicks,
    };
    for (const [name, value] of Object.entries(limits)) {
        if (!Number.isSafeInteger(value) || value <= 0)
            fail(`limits.${name}`, "expected a positive safe integer");
    }
    return limits;
}
function utf8ByteLength(value) {
    let bytes = 0;
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code <= 0x7f)
            bytes += 1;
        else if (code <= 0x7ff)
            bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff) {
            const next = value.charCodeAt(index + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                bytes += 4;
                index += 1;
            }
            else
                bytes += 3;
        }
        else
            bytes += 3;
    }
    return bytes;
}
function parseVector(value, path) {
    const record = expectRecord(value, path, ["x", "y"]);
    return {
        x: expectFiniteNumber(record.x, `${path}.x`),
        y: expectFiniteNumber(record.y, `${path}.y`),
    };
}
function parseConfig(value, path) {
    const record = expectRecord(value, path, ["table", "puck", "mallet", "rules", "physics"]);
    const table = expectRecord(record.table, `${path}.table`, [
        "width",
        "height",
        "wallInset",
        "cornerRadius",
        "goalWidth",
        "goalDepth",
        "postRadius",
    ]);
    const puck = expectRecord(record.puck, `${path}.puck`, [
        "radius",
        "mass",
        "maxSpeed",
        "frictionPerSecond",
        "wallRestitution",
        "postRestitution",
        "malletRestitution",
        "stuckSpeed",
        "stuckTicks",
    ]);
    const mallet = expectRecord(record.mallet, `${path}.mallet`, [
        "radius",
        "mass",
        "maxSpeed",
        "maxAcceleration",
        "centerLineGap",
    ]);
    const rules = expectRecord(record.rules, `${path}.rules`, [
        "mode",
        "pointsToWin",
        "durationTicks",
        "initialCountdownTicks",
        "restartCountdownTicks",
        "goalPauseTicks",
    ]);
    const physics = expectRecord(record.physics, `${path}.physics`, [
        "tickRate",
        "maxCollisionsPerTick",
        "collisionEpsilon",
        "statePrecision",
    ]);
    const durationTicks = rules.durationTicks === null
        ? null
        : expectSafeInteger(rules.durationTicks, `${path}.rules.durationTicks`, 1);
    const config = {
        table: {
            width: expectFiniteNumber(table.width, `${path}.table.width`),
            height: expectFiniteNumber(table.height, `${path}.table.height`),
            wallInset: expectFiniteNumber(table.wallInset, `${path}.table.wallInset`),
            cornerRadius: expectFiniteNumber(table.cornerRadius, `${path}.table.cornerRadius`),
            goalWidth: expectFiniteNumber(table.goalWidth, `${path}.table.goalWidth`),
            goalDepth: expectFiniteNumber(table.goalDepth, `${path}.table.goalDepth`),
            postRadius: expectFiniteNumber(table.postRadius, `${path}.table.postRadius`),
        },
        puck: {
            radius: expectFiniteNumber(puck.radius, `${path}.puck.radius`),
            mass: expectFiniteNumber(puck.mass, `${path}.puck.mass`),
            maxSpeed: expectFiniteNumber(puck.maxSpeed, `${path}.puck.maxSpeed`),
            frictionPerSecond: expectFiniteNumber(puck.frictionPerSecond, `${path}.puck.frictionPerSecond`),
            wallRestitution: expectFiniteNumber(puck.wallRestitution, `${path}.puck.wallRestitution`),
            postRestitution: expectFiniteNumber(puck.postRestitution, `${path}.puck.postRestitution`),
            malletRestitution: expectFiniteNumber(puck.malletRestitution, `${path}.puck.malletRestitution`),
            stuckSpeed: expectFiniteNumber(puck.stuckSpeed, `${path}.puck.stuckSpeed`),
            stuckTicks: expectSafeInteger(puck.stuckTicks, `${path}.puck.stuckTicks`),
        },
        mallet: {
            radius: expectFiniteNumber(mallet.radius, `${path}.mallet.radius`),
            mass: expectFiniteNumber(mallet.mass, `${path}.mallet.mass`),
            maxSpeed: expectFiniteNumber(mallet.maxSpeed, `${path}.mallet.maxSpeed`),
            maxAcceleration: expectFiniteNumber(mallet.maxAcceleration, `${path}.mallet.maxAcceleration`),
            centerLineGap: expectFiniteNumber(mallet.centerLineGap, `${path}.mallet.centerLineGap`),
        },
        rules: {
            mode: expectEnum(rules.mode, `${path}.rules.mode`, MATCH_MODES),
            pointsToWin: expectSafeInteger(rules.pointsToWin, `${path}.rules.pointsToWin`, 1),
            durationTicks,
            initialCountdownTicks: expectSafeInteger(rules.initialCountdownTicks, `${path}.rules.initialCountdownTicks`),
            restartCountdownTicks: expectSafeInteger(rules.restartCountdownTicks, `${path}.rules.restartCountdownTicks`),
            goalPauseTicks: expectSafeInteger(rules.goalPauseTicks, `${path}.rules.goalPauseTicks`),
        },
        physics: {
            tickRate: expectSafeInteger(physics.tickRate, `${path}.physics.tickRate`, 1),
            maxCollisionsPerTick: expectSafeInteger(physics.maxCollisionsPerTick, `${path}.physics.maxCollisionsPerTick`, 1),
            collisionEpsilon: expectFiniteNumber(physics.collisionEpsilon, `${path}.physics.collisionEpsilon`),
            statePrecision: expectFiniteNumber(physics.statePrecision, `${path}.physics.statePrecision`),
        },
    };
    try {
        assertValidConfig(config);
    }
    catch (error) {
        fail(path, error instanceof Error ? error.message : "invalid game configuration");
    }
    return resolveGameConfig(config);
}
function parsePlayerRuntime(value, path) {
    const record = expectRecord(value, path, ["connected", "ready", "lastProcessedSequence"]);
    return {
        connected: expectBoolean(record.connected, `${path}.connected`),
        ready: expectBoolean(record.ready, `${path}.ready`),
        lastProcessedSequence: expectSafeInteger(record.lastProcessedSequence, `${path}.lastProcessedSequence`, -1),
    };
}
function parseMallet(value, path) {
    const record = expectRecord(value, path, [
        "position",
        "previousPosition",
        "velocity",
        "target",
        "radius",
        "mass",
    ]);
    const radius = expectFiniteNumber(record.radius, `${path}.radius`);
    const mass = expectFiniteNumber(record.mass, `${path}.mass`);
    if (radius <= 0)
        fail(`${path}.radius`, "expected a positive number");
    if (mass <= 0)
        fail(`${path}.mass`, "expected a positive number");
    return {
        position: parseVector(record.position, `${path}.position`),
        previousPosition: parseVector(record.previousPosition, `${path}.previousPosition`),
        velocity: parseVector(record.velocity, `${path}.velocity`),
        target: parseVector(record.target, `${path}.target`),
        radius,
        mass,
    };
}
function parsePuck(value, path) {
    const record = expectRecord(value, path, [
        "position",
        "velocity",
        "radius",
        "mass",
        "active",
        "lastTouchedBy",
        "lastCollisionTick",
        "stationaryTicks",
    ]);
    const radius = expectFiniteNumber(record.radius, `${path}.radius`);
    const mass = expectFiniteNumber(record.mass, `${path}.mass`);
    if (radius <= 0)
        fail(`${path}.radius`, "expected a positive number");
    if (mass <= 0)
        fail(`${path}.mass`, "expected a positive number");
    return {
        position: parseVector(record.position, `${path}.position`),
        velocity: parseVector(record.velocity, `${path}.velocity`),
        radius,
        mass,
        active: expectBoolean(record.active, `${path}.active`),
        lastTouchedBy: expectNullableEnum(record.lastTouchedBy, `${path}.lastTouchedBy`, PLAYER_SLOTS),
        lastCollisionTick: record.lastCollisionTick === null
            ? null
            : expectSafeInteger(record.lastCollisionTick, `${path}.lastCollisionTick`),
        stationaryTicks: expectSafeInteger(record.stationaryTicks, `${path}.stationaryTicks`),
    };
}
function parseLastGoal(value, path) {
    if (value === null)
        return null;
    const record = expectRecord(value, path, ["scorer", "tick", "puckPosition"]);
    return {
        scorer: expectEnum(record.scorer, `${path}.scorer`, PLAYER_SLOTS),
        tick: expectSafeInteger(record.tick, `${path}.tick`),
        puckPosition: parseVector(record.puckPosition, `${path}.puckPosition`),
    };
}
function parseState(value, path, limits) {
    const record = expectRecord(value, path, [
        "matchId",
        "seed",
        "physicsVersion",
        "protocolVersion",
        "tick",
        "phase",
        "pausedFromPhase",
        "phaseTicksRemaining",
        "remainingTicks",
        "score",
        "winner",
        "finishReason",
        "players",
        "mallets",
        "puck",
        "lastGoal",
    ]);
    if (record.physicsVersion !== PHYSICS_VERSION) {
        throw new ReplayArtifactVersionError(`${path}.physicsVersion`, PHYSICS_VERSION, record.physicsVersion);
    }
    if (record.protocolVersion !== PROTOCOL_VERSION) {
        throw new ReplayArtifactVersionError(`${path}.protocolVersion`, PROTOCOL_VERSION, record.protocolVersion);
    }
    const score = expectRecord(record.score, `${path}.score`, ["PLAYER_A", "PLAYER_B"]);
    const players = expectRecord(record.players, `${path}.players`, ["PLAYER_A", "PLAYER_B"]);
    const mallets = expectRecord(record.mallets, `${path}.mallets`, ["PLAYER_A", "PLAYER_B"]);
    const state = {
        matchId: expectString(record.matchId, `${path}.matchId`, limits),
        seed: expectSafeInteger(record.seed, `${path}.seed`),
        physicsVersion: PHYSICS_VERSION,
        protocolVersion: PROTOCOL_VERSION,
        tick: expectSafeInteger(record.tick, `${path}.tick`),
        phase: expectEnum(record.phase, `${path}.phase`, MATCH_PHASES),
        pausedFromPhase: expectNullableEnum(record.pausedFromPhase, `${path}.pausedFromPhase`, PAUSABLE_PHASES),
        phaseTicksRemaining: expectSafeInteger(record.phaseTicksRemaining, `${path}.phaseTicksRemaining`),
        remainingTicks: record.remainingTicks === null
            ? null
            : expectSafeInteger(record.remainingTicks, `${path}.remainingTicks`),
        score: {
            PLAYER_A: expectSafeInteger(score.PLAYER_A, `${path}.score.PLAYER_A`),
            PLAYER_B: expectSafeInteger(score.PLAYER_B, `${path}.score.PLAYER_B`),
        },
        winner: expectNullableEnum(record.winner, `${path}.winner`, PLAYER_SLOTS),
        finishReason: expectNullableEnum(record.finishReason, `${path}.finishReason`, FINISH_REASONS),
        players: {
            PLAYER_A: parsePlayerRuntime(players.PLAYER_A, `${path}.players.PLAYER_A`),
            PLAYER_B: parsePlayerRuntime(players.PLAYER_B, `${path}.players.PLAYER_B`),
        },
        mallets: {
            PLAYER_A: parseMallet(mallets.PLAYER_A, `${path}.mallets.PLAYER_A`),
            PLAYER_B: parseMallet(mallets.PLAYER_B, `${path}.mallets.PLAYER_B`),
        },
        puck: parsePuck(record.puck, `${path}.puck`),
        lastGoal: parseLastGoal(record.lastGoal, `${path}.lastGoal`),
    };
    if (state.phase === "paused" && state.pausedFromPhase === null) {
        fail(`${path}.pausedFromPhase`, "paused state must identify its previous phase");
    }
    if (state.phase !== "paused" && state.pausedFromPhase !== null) {
        fail(`${path}.pausedFromPhase`, "only a paused state may identify a previous phase");
    }
    if ((state.phase === "finished") !== (state.finishReason !== null)) {
        fail(`${path}.finishReason`, "must be present exactly when phase is finished");
    }
    if (state.phase !== "finished" && state.winner !== null) {
        fail(`${path}.winner`, "must be null until the match is finished");
    }
    if (state.lastGoal !== null && state.lastGoal.tick > state.tick) {
        fail(`${path}.lastGoal.tick`, "cannot be after the state tick");
    }
    return state;
}
function parseInput(value, path) {
    const record = expectRecord(value, path, ["player", "sequence", "targetX", "targetY"], ["clientTime"]);
    const targetX = expectFiniteNumber(record.targetX, `${path}.targetX`);
    const targetY = expectFiniteNumber(record.targetY, `${path}.targetY`);
    if (targetX < 0 || targetX > 1)
        fail(`${path}.targetX`, "expected a normalized value in [0, 1]");
    if (targetY < 0 || targetY > 1)
        fail(`${path}.targetY`, "expected a normalized value in [0, 1]");
    const input = {
        player: expectEnum(record.player, `${path}.player`, PLAYER_SLOTS),
        sequence: expectSafeInteger(record.sequence, `${path}.sequence`),
        targetX,
        targetY,
    };
    return record.clientTime === undefined
        ? input
        : { ...input, clientTime: expectFiniteNumber(record.clientTime, `${path}.clientTime`) };
}
function parseControlCommand(value, path) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return fail(path, "expected a control command object");
    }
    const type = value.type;
    if (type === "SET_PLAYER_CONNECTED") {
        const record = expectRecord(value, path, ["type", "player", "connected"]);
        return {
            type,
            player: expectEnum(record.player, `${path}.player`, PLAYER_SLOTS),
            connected: expectBoolean(record.connected, `${path}.connected`),
        };
    }
    if (type === "SET_PLAYER_READY") {
        const record = expectRecord(value, path, ["type", "player", "ready"]);
        return {
            type,
            player: expectEnum(record.player, `${path}.player`, PLAYER_SLOTS),
            ready: expectBoolean(record.ready, `${path}.ready`),
        };
    }
    if (type === "PAUSE" || type === "RESUME" || type === "CANCEL") {
        expectRecord(value, path, ["type"]);
        return { type };
    }
    if (type === "FORFEIT") {
        const record = expectRecord(value, path, ["type", "forfeitingPlayer"]);
        return {
            type,
            forfeitingPlayer: expectEnum(record.forfeitingPlayer, `${path}.forfeitingPlayer`, PLAYER_SLOTS),
        };
    }
    return fail(`${path}.type`, "expected a supported control command type");
}
function parseControlTransition(value, path) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return fail(path, "expected a control transition object");
    }
    const raw = value;
    const evidence = {
        afterFrame: expectSafeInteger(raw.afterFrame, `${path}.afterFrame`),
        tick: expectSafeInteger(raw.tick, `${path}.tick`),
        stateHash: expectHash(raw.stateHash, `${path}.stateHash`),
    };
    if (raw.type === "SET_PLAYER_CONNECTED") {
        const record = expectRecord(value, path, [
            "type",
            "afterFrame",
            "tick",
            "stateHash",
            "player",
            "connected",
        ]);
        return {
            ...evidence,
            type: raw.type,
            player: expectEnum(record.player, `${path}.player`, PLAYER_SLOTS),
            connected: expectBoolean(record.connected, `${path}.connected`),
        };
    }
    if (raw.type === "SET_PLAYER_READY") {
        const record = expectRecord(value, path, [
            "type",
            "afterFrame",
            "tick",
            "stateHash",
            "player",
            "ready",
        ]);
        return {
            ...evidence,
            type: raw.type,
            player: expectEnum(record.player, `${path}.player`, PLAYER_SLOTS),
            ready: expectBoolean(record.ready, `${path}.ready`),
        };
    }
    if (raw.type === "PAUSE" || raw.type === "RESUME" || raw.type === "CANCEL") {
        expectRecord(value, path, ["type", "afterFrame", "tick", "stateHash"]);
        return { ...evidence, type: raw.type };
    }
    if (raw.type === "FORFEIT") {
        const record = expectRecord(value, path, [
            "type",
            "afterFrame",
            "tick",
            "stateHash",
            "forfeitingPlayer",
        ]);
        return {
            ...evidence,
            type: raw.type,
            forfeitingPlayer: expectEnum(record.forfeitingPlayer, `${path}.forfeitingPlayer`, PLAYER_SLOTS),
        };
    }
    return fail(`${path}.type`, "expected a supported control transition type");
}
function commandFromTransition(transition) {
    switch (transition.type) {
        case "SET_PLAYER_CONNECTED":
            return { type: transition.type, player: transition.player, connected: transition.connected };
        case "SET_PLAYER_READY":
            return { type: transition.type, player: transition.player, ready: transition.ready };
        case "PAUSE":
        case "RESUME":
        case "CANCEL":
            return { type: transition.type };
        case "FORFEIT":
            return { type: transition.type, forfeitingPlayer: transition.forfeitingPlayer };
    }
}
function applyControlCommand(state, command, config) {
    switch (command.type) {
        case "SET_PLAYER_CONNECTED":
            return setPlayerConnected(state, command.player, command.connected);
        case "SET_PLAYER_READY":
            return setPlayerReady(state, command.player, command.ready, config);
        case "PAUSE":
            return pauseMatch(state);
        case "RESUME":
            return resumeMatch(state);
        case "FORFEIT":
            return forfeitMatch(state, command.forfeitingPlayer);
        case "CANCEL":
            return cancelMatch(state);
    }
}
function parseFrame(value, path, limits) {
    const record = expectRecord(value, path, ["tick", "inputs", "stateHash"]);
    const inputs = expectArray(record.inputs, `${path}.inputs`, limits.maxInputsPerFrame);
    return {
        tick: expectSafeInteger(record.tick, `${path}.tick`),
        inputs: inputs.map((input, index) => parseInput(input, `${path}.inputs[${index}]`)),
        stateHash: expectHash(record.stateHash, `${path}.stateHash`),
    };
}
function parseImportantEvent(value, path) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return fail(path, "expected an event object");
    }
    const type = value.type;
    if (type === "GOAL") {
        const record = expectRecord(value, path, ["type", "tick", "scorer", "scoreA", "scoreB", "stateHash"]);
        return {
            type,
            tick: expectSafeInteger(record.tick, `${path}.tick`),
            scorer: expectEnum(record.scorer, `${path}.scorer`, PLAYER_SLOTS),
            scoreA: expectSafeInteger(record.scoreA, `${path}.scoreA`),
            scoreB: expectSafeInteger(record.scoreB, `${path}.scoreB`),
            stateHash: expectHash(record.stateHash, `${path}.stateHash`),
        };
    }
    if (type === "PHASE_CHANGED") {
        const record = expectRecord(value, path, ["type", "tick", "from", "to"]);
        return {
            type,
            tick: expectSafeInteger(record.tick, `${path}.tick`),
            from: expectEnum(record.from, `${path}.from`, MATCH_PHASES),
            to: expectEnum(record.to, `${path}.to`, MATCH_PHASES),
        };
    }
    if (type === "FINISHED") {
        const record = expectRecord(value, path, [
            "type",
            "tick",
            "winner",
            "reason",
            "scoreA",
            "scoreB",
            "stateHash",
        ]);
        return {
            type,
            tick: expectSafeInteger(record.tick, `${path}.tick`),
            winner: expectNullableEnum(record.winner, `${path}.winner`, PLAYER_SLOTS),
            reason: expectEnum(record.reason, `${path}.reason`, FINISH_REASONS),
            scoreA: expectSafeInteger(record.scoreA, `${path}.scoreA`),
            scoreB: expectSafeInteger(record.scoreB, `${path}.scoreB`),
            stateHash: expectHash(record.stateHash, `${path}.stateHash`),
        };
    }
    if (type === "PUCK_RESET") {
        const record = expectRecord(value, path, ["type", "tick", "reason"]);
        return {
            type,
            tick: expectSafeInteger(record.tick, `${path}.tick`),
            reason: expectEnum(record.reason, `${path}.reason`, ["goal", "stuck", "manual"]),
        };
    }
    return fail(`${path}.type`, "expected an important event type");
}
function parseStepEvent(value, path) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return fail(path, "expected an event object");
    }
    const type = value.type;
    if (type !== "COLLISION")
        return parseImportantEvent(value, path);
    const record = expectRecord(value, path, ["type", "tick", "collider"], ["player"]);
    const collider = expectEnum(record.collider, `${path}.collider`, ["wall", "post", "mallet"]);
    const player = record.player === undefined
        ? undefined
        : expectEnum(record.player, `${path}.player`, PLAYER_SLOTS);
    if (collider === "mallet" && player === undefined) {
        fail(`${path}.player`, "mallet collisions must identify a player");
    }
    if (collider !== "mallet" && player !== undefined) {
        fail(`${path}.player`, "only mallet collisions may identify a player");
    }
    const event = {
        type: "COLLISION",
        tick: expectSafeInteger(record.tick, `${path}.tick`),
        collider,
    };
    return player === undefined ? event : { ...event, player };
}
function parseCheckpoint(value, path, limits) {
    const record = expectRecord(value, path, ["sourceIndex", "tick", "state", "stateHash"]);
    return {
        sourceIndex: expectSafeInteger(record.sourceIndex, `${path}.sourceIndex`),
        tick: expectSafeInteger(record.tick, `${path}.tick`),
        state: parseState(record.state, `${path}.state`, limits),
        stateHash: expectHash(record.stateHash, `${path}.stateHash`),
    };
}
function parseMetadata(value, path, limits) {
    const record = expectRecord(value, path, [
        "matchId",
        "mode",
        "region",
        "playerAId",
        "playerBId",
        "startedAt",
        "finishedAt",
    ]);
    return {
        matchId: expectString(record.matchId, `${path}.matchId`, limits),
        mode: expectEnum(record.mode, `${path}.mode`, MATCH_MODES),
        region: expectNullableString(record.region, `${path}.region`, limits),
        playerAId: expectNullableString(record.playerAId, `${path}.playerAId`, limits),
        playerBId: expectNullableString(record.playerBId, `${path}.playerBId`, limits),
        startedAt: expectTimestamp(record.startedAt, `${path}.startedAt`, limits),
        finishedAt: expectTimestamp(record.finishedAt, `${path}.finishedAt`, limits),
    };
}
function parseFinal(value, path) {
    const record = expectRecord(value, path, ["tick", "scoreA", "scoreB", "winner", "reason", "stateHash"]);
    return {
        tick: expectSafeInteger(record.tick, `${path}.tick`),
        scoreA: expectSafeInteger(record.scoreA, `${path}.scoreA`),
        scoreB: expectSafeInteger(record.scoreB, `${path}.scoreB`),
        winner: expectNullableEnum(record.winner, `${path}.winner`, PLAYER_SLOTS),
        reason: expectNullableEnum(record.reason, `${path}.reason`, FINISH_REASONS),
        stateHash: expectHash(record.stateHash, `${path}.stateHash`),
    };
}
function artifactPayload(artifact) {
    return {
        schemaVersion: artifact.schemaVersion,
        physicsVersion: artifact.physicsVersion,
        protocolVersion: artifact.protocolVersion,
        metadata: artifact.metadata,
        initial: artifact.initial,
        checkpointIntervalTicks: artifact.checkpointIntervalTicks,
        frames: artifact.frames,
        controlTransitions: artifact.controlTransitions,
        importantEvents: artifact.importantEvents,
        checkpoints: artifact.checkpoints,
        final: artifact.final,
    };
}
export function computeReplayArtifactHash(artifact) {
    const payload = "artifactHash" in artifact ? artifactPayload(artifact) : artifact;
    return deterministicJsonHash(payload);
}
function sealPayload(payload) {
    return { ...payload, artifactHash: computeReplayArtifactHash(payload) };
}
function assertStateMatchesConfig(state, config, path) {
    if (state.puck.radius !== config.puck.radius || state.puck.mass !== config.puck.mass) {
        fail(`${path}.puck`, "radius and mass must match the replay configuration");
    }
    for (const player of PLAYER_SLOTS) {
        if (state.mallets[player].radius !== config.mallet.radius
            || state.mallets[player].mass !== config.mallet.mass) {
            fail(`${path}.mallets.${player}`, "radius and mass must match the replay configuration");
        }
    }
}
function replaySourceDescriptors(artifact) {
    const descriptors = [];
    let controlIndex = 0;
    let sourceIndex = 0;
    for (let afterFrame = 0; afterFrame <= artifact.frames.length; afterFrame += 1) {
        while (artifact.controlTransitions[controlIndex]?.afterFrame === afterFrame) {
            const transition = artifact.controlTransitions[controlIndex];
            if (transition === undefined)
                break;
            sourceIndex += 1;
            descriptors.push({
                sourceIndex,
                kind: "CONTROL",
                itemIndex: controlIndex,
                tick: transition.tick,
                stateHash: transition.stateHash,
            });
            controlIndex += 1;
        }
        const frame = artifact.frames[afterFrame];
        if (frame !== undefined) {
            sourceIndex += 1;
            descriptors.push({
                sourceIndex,
                kind: "FRAME",
                itemIndex: afterFrame,
                tick: frame.tick,
                stateHash: frame.stateHash,
            });
        }
    }
    return descriptors;
}
function expectedCheckpointSources(artifact, sources) {
    const required = [{
            sourceIndex: 0,
            kind: "FRAME",
            itemIndex: -1,
            tick: artifact.initial.state.tick,
            stateHash: artifact.initial.stateHash,
        }];
    for (const source of sources) {
        if (source.kind === "FRAME"
            && (source.tick - artifact.initial.state.tick) % artifact.checkpointIntervalTicks === 0) {
            required.push(source);
        }
    }
    const finalSource = sources[sources.length - 1];
    if (artifact.initial.state.phase === "finished" && finalSource !== undefined) {
        fail("$", "a replay with a finished initial state cannot contain additional sources");
    }
    if (artifact.final.reason !== null
        && finalSource !== undefined
        && required[required.length - 1]?.sourceIndex !== finalSource.sourceIndex) {
        required.push(finalSource);
    }
    return required;
}
/** Strictly validates and clones an already-decoded replay value. */
export function parseReplayArtifact(value, limitOverrides = {}) {
    const limits = resolveLimits(limitOverrides);
    const record = expectRecord(value, "$", [
        "schemaVersion",
        "physicsVersion",
        "protocolVersion",
        "metadata",
        "initial",
        "checkpointIntervalTicks",
        "frames",
        "controlTransitions",
        "importantEvents",
        "checkpoints",
        "final",
        "artifactHash",
    ]);
    if (record.schemaVersion !== REPLAY_ARTIFACT_VERSION) {
        throw new ReplayArtifactVersionError("$.schemaVersion", REPLAY_ARTIFACT_VERSION, record.schemaVersion);
    }
    if (record.physicsVersion !== PHYSICS_VERSION) {
        throw new ReplayArtifactVersionError("$.physicsVersion", PHYSICS_VERSION, record.physicsVersion);
    }
    if (record.protocolVersion !== PROTOCOL_VERSION) {
        throw new ReplayArtifactVersionError("$.protocolVersion", PROTOCOL_VERSION, record.protocolVersion);
    }
    const initialRecord = expectRecord(record.initial, "$.initial", ["seed", "config", "state", "stateHash"]);
    const config = parseConfig(initialRecord.config, "$.initial.config");
    const state = parseState(initialRecord.state, "$.initial.state", limits);
    const initial = {
        seed: expectSafeInteger(initialRecord.seed, "$.initial.seed"),
        config,
        state,
        stateHash: expectHash(initialRecord.stateHash, "$.initial.stateHash"),
    };
    const checkpointIntervalTicks = expectSafeInteger(record.checkpointIntervalTicks, "$.checkpointIntervalTicks", 1);
    if (checkpointIntervalTicks > limits.maxCheckpointIntervalTicks) {
        fail("$.checkpointIntervalTicks", `exceeds maximum ${limits.maxCheckpointIntervalTicks}`);
    }
    const frameValues = expectArray(record.frames, "$.frames", limits.maxFrames);
    const frames = [];
    let totalInputs = 0;
    for (let index = 0; index < frameValues.length; index += 1) {
        const frame = parseFrame(frameValues[index], `$.frames[${index}]`, limits);
        totalInputs += frame.inputs.length;
        if (totalInputs > limits.maxInputs)
            fail("$.frames", `exceeds aggregate input count ${limits.maxInputs}`);
        frames.push(frame);
    }
    const controlValues = expectArray(record.controlTransitions, "$.controlTransitions", limits.maxControlTransitions);
    const controlTransitions = controlValues.map((transition, index) => parseControlTransition(transition, `$.controlTransitions[${index}]`));
    const eventValues = expectArray(record.importantEvents, "$.importantEvents", limits.maxImportantEvents);
    const importantEvents = eventValues.map((event, index) => parseImportantEvent(event, `$.importantEvents[${index}]`));
    const checkpointValues = expectArray(record.checkpoints, "$.checkpoints", limits.maxCheckpoints);
    const checkpoints = checkpointValues.map((checkpoint, index) => parseCheckpoint(checkpoint, `$.checkpoints[${index}]`, limits));
    const artifact = {
        schemaVersion: REPLAY_ARTIFACT_VERSION,
        physicsVersion: PHYSICS_VERSION,
        protocolVersion: PROTOCOL_VERSION,
        metadata: parseMetadata(record.metadata, "$.metadata", limits),
        initial,
        checkpointIntervalTicks,
        frames,
        controlTransitions,
        importantEvents,
        checkpoints,
        final: parseFinal(record.final, "$.final"),
        artifactHash: expectHash(record.artifactHash, "$.artifactHash"),
    };
    if (artifact.metadata.matchId !== artifact.initial.state.matchId) {
        fail("$.metadata.matchId", "must match initial.state.matchId");
    }
    if (artifact.metadata.mode !== artifact.initial.config.rules.mode) {
        fail("$.metadata.mode", "must match initial.config.rules.mode");
    }
    if (artifact.initial.seed !== artifact.initial.state.seed) {
        fail("$.initial.seed", "must match initial.state.seed");
    }
    assertStateMatchesConfig(artifact.initial.state, artifact.initial.config, "$.initial.state");
    const initialHash = hashGameState(artifact.initial.state);
    if (initialHash !== artifact.initial.stateHash) {
        throw new ReplayDivergenceError(artifact.initial.state.tick, artifact.initial.stateHash, initialHash);
    }
    let previousTick = artifact.initial.state.tick;
    for (let index = 0; index < artifact.frames.length; index += 1) {
        const frame = artifact.frames[index];
        if (frame === undefined)
            continue;
        if (frame.tick !== previousTick + 1) {
            fail(`$.frames[${index}].tick`, `must be contiguous after tick ${previousTick}`);
        }
        previousTick = frame.tick;
    }
    let previousAfterFrame = 0;
    for (let index = 0; index < artifact.controlTransitions.length; index += 1) {
        const transition = artifact.controlTransitions[index];
        if (transition === undefined)
            continue;
        if (transition.afterFrame < previousAfterFrame) {
            fail(`$.controlTransitions[${index}].afterFrame`, "control transitions must be ordered");
        }
        if (transition.afterFrame > artifact.frames.length) {
            fail(`$.controlTransitions[${index}].afterFrame`, "cannot be after the available frames");
        }
        const expectedTick = artifact.initial.state.tick + transition.afterFrame;
        if (transition.tick !== expectedTick) {
            fail(`$.controlTransitions[${index}].tick`, `expected tick ${expectedTick}`);
        }
        previousAfterFrame = transition.afterFrame;
    }
    const sources = replaySourceDescriptors(artifact);
    if (sources.length !== artifact.frames.length + artifact.controlTransitions.length) {
        fail("$.controlTransitions", "could not place every transition in the ordered source stream");
    }
    const finalSource = sources[sources.length - 1];
    const expectedFinalTick = finalSource?.tick ?? artifact.initial.state.tick;
    const expectedFinalHash = finalSource?.stateHash ?? artifact.initial.stateHash;
    if (artifact.final.tick !== expectedFinalTick) {
        fail("$.final.tick", `must equal final source tick ${expectedFinalTick}`);
    }
    if (artifact.final.stateHash !== expectedFinalHash) {
        fail("$.final.stateHash", "must equal the last recorded source state hash");
    }
    if (artifact.final.reason === null && artifact.final.winner !== null) {
        fail("$.final.winner", "must be null while the replay is unfinished");
    }
    if (artifact.final.reason === "cancelled" && artifact.final.winner !== null) {
        fail("$.final.winner", "must be null for a cancelled match");
    }
    if ((artifact.final.reason === "score" || artifact.final.reason === "forfeit")
        && artifact.final.winner === null) {
        fail("$.final.winner", `must identify a winner for reason ${artifact.final.reason}`);
    }
    if (artifact.final.reason === null && artifact.metadata.finishedAt !== null) {
        fail("$.metadata.finishedAt", "must be null while the replay is unfinished");
    }
    if (artifact.metadata.startedAt !== null
        && artifact.metadata.finishedAt !== null
        && timestampOrderKey(artifact.metadata.finishedAt) < timestampOrderKey(artifact.metadata.startedAt)) {
        fail("$.metadata.finishedAt", "cannot be before metadata.startedAt");
    }
    let previousEventTick = artifact.initial.state.tick;
    const frameHashByTick = new Map(artifact.frames.map((frame) => [frame.tick, frame.stateHash]));
    for (let index = 0; index < artifact.importantEvents.length; index += 1) {
        const event = artifact.importantEvents[index];
        if (event === undefined)
            continue;
        if (event.tick < previousEventTick) {
            fail(`$.importantEvents[${index}].tick`, "events must be ordered by tick");
        }
        const frameHash = frameHashByTick.get(event.tick);
        if (frameHash === undefined) {
            fail(`$.importantEvents[${index}].tick`, "must reference a replay frame");
        }
        if ((event.type === "GOAL" || event.type === "FINISHED") && event.stateHash !== frameHash) {
            fail(`$.importantEvents[${index}].stateHash`, "must match its replay frame state hash");
        }
        previousEventTick = event.tick;
    }
    const terminalSources = [];
    for (const source of sources) {
        if (source.kind === "CONTROL") {
            const transition = artifact.controlTransitions[source.itemIndex];
            if (transition?.type === "FORFEIT" || transition?.type === "CANCEL") {
                terminalSources.push(source);
            }
        }
        else {
            const frame = artifact.frames[source.itemIndex];
            if (frame !== undefined
                && artifact.importantEvents.some((event) => event.type === "FINISHED" && event.tick === frame.tick)) {
                terminalSources.push(source);
            }
        }
    }
    if (terminalSources.length > 1)
        fail("$", "replay contains more than one terminal source");
    const terminalSource = terminalSources[0];
    if (terminalSource !== undefined && terminalSource.sourceIndex !== finalSource?.sourceIndex) {
        fail("$", "no replay source may follow a terminal transition");
    }
    if (artifact.initial.state.phase !== "finished") {
        if ((artifact.final.reason !== null) !== (terminalSource !== undefined)) {
            fail("$.final.reason", "must correspond to exactly one terminal replay source");
        }
    }
    if (terminalSource?.kind === "CONTROL") {
        const transition = artifact.controlTransitions[terminalSource.itemIndex];
        if (transition?.type === "FORFEIT" && artifact.final.reason !== "forfeit") {
            fail("$.final.reason", "FORFEIT must produce reason forfeit");
        }
        if (transition?.type === "CANCEL" && artifact.final.reason !== "cancelled") {
            fail("$.final.reason", "CANCEL must produce reason cancelled");
        }
    }
    const requiredCheckpointSources = expectedCheckpointSources(artifact, sources);
    if (artifact.checkpoints.length !== requiredCheckpointSources.length) {
        fail("$.checkpoints", `expected checkpoints at source indices ${requiredCheckpointSources.map((source) => source.sourceIndex).join(", ")}`);
    }
    for (let index = 0; index < artifact.checkpoints.length; index += 1) {
        const checkpoint = artifact.checkpoints[index];
        const expectedSource = requiredCheckpointSources[index];
        if (checkpoint === undefined || expectedSource === undefined)
            continue;
        if (checkpoint.sourceIndex !== expectedSource.sourceIndex) {
            fail(`$.checkpoints[${index}].sourceIndex`, `expected checkpoint source index ${expectedSource.sourceIndex}`);
        }
        if (checkpoint.tick !== expectedSource.tick || checkpoint.state.tick !== checkpoint.tick) {
            fail(`$.checkpoints[${index}].tick`, `expected checkpoint tick ${expectedSource.tick}`);
        }
        if (checkpoint.state.matchId !== artifact.metadata.matchId
            || checkpoint.state.seed !== artifact.initial.seed) {
            fail(`$.checkpoints[${index}].state`, "matchId and seed must match the initial state");
        }
        assertStateMatchesConfig(checkpoint.state, artifact.initial.config, `$.checkpoints[${index}].state`);
        const checkpointHash = hashGameState(checkpoint.state);
        if (checkpointHash !== checkpoint.stateHash) {
            throw new ReplayDivergenceError(checkpoint.tick, checkpoint.stateHash, checkpointHash);
        }
        if (expectedSource.stateHash !== checkpoint.stateHash) {
            fail(`$.checkpoints[${index}].stateHash`, "must match its recorded source state hash");
        }
    }
    if (artifact.checkpoints[0] === undefined
        || artifact.checkpoints[0].sourceIndex !== 0
        || stableJsonStringify(artifact.checkpoints[0].state) !== stableJsonStringify(artifact.initial.state)) {
        fail("$.checkpoints[0].state", "must equal the initial state");
    }
    const expectedArtifactHash = computeReplayArtifactHash(artifact);
    if (expectedArtifactHash !== artifact.artifactHash) {
        fail("$.artifactHash", `expected ${expectedArtifactHash}`);
    }
    const canonicalBytes = utf8ByteLength(stableJsonStringify(artifact));
    if (canonicalBytes > limits.maxBytes)
        fail("$", `exceeds maximum encoded size ${limits.maxBytes} bytes`);
    return artifact;
}
/** Parses bounded JSON and rejects unknown fields, malformed values, and checksum tampering. */
export function parseReplayArtifactJson(json, limitOverrides = {}) {
    const limits = resolveLimits(limitOverrides);
    if (typeof json !== "string")
        fail("$", "expected a JSON string");
    const bytes = utf8ByteLength(json);
    if (bytes > limits.maxBytes)
        fail("$", `exceeds maximum encoded size ${limits.maxBytes} bytes`);
    let value;
    try {
        value = JSON.parse(json);
    }
    catch {
        fail("$", "invalid JSON");
    }
    return parseReplayArtifact(value, limits);
}
/** Emits canonical key-sorted JSON after performing the same strict validation as the parser. */
export function serializeReplayArtifact(value, limitOverrides = {}) {
    const artifact = parseReplayArtifact(value, limitOverrides);
    return stableJsonStringify(artifact);
}
function cloneInput(input) {
    const base = {
        player: input.player,
        sequence: input.sequence,
        targetX: input.targetX,
        targetY: input.targetY,
    };
    return input.clientTime === undefined ? base : { ...base, clientTime: input.clientTime };
}
function cloneFrame(frame) {
    return { tick: frame.tick, inputs: frame.inputs.map(cloneInput), stateHash: frame.stateHash };
}
function isImportantEvent(event) {
    return event.type !== "COLLISION";
}
function cloneImportantEvent(event) {
    switch (event.type) {
        case "GOAL":
            return { ...event };
        case "PHASE_CHANGED":
            return { ...event };
        case "FINISHED":
            return { ...event };
        case "PUCK_RESET":
            return { ...event };
    }
}
function cloneCheckpoint(checkpoint) {
    return {
        sourceIndex: checkpoint.sourceIndex,
        tick: checkpoint.tick,
        state: cloneGameState(checkpoint.state),
        stateHash: checkpoint.stateHash,
    };
}
function cloneControlTransition(transition) {
    return { ...transition };
}
function finalFromState(state, stateHash) {
    return {
        tick: state.tick,
        scoreA: state.score.PLAYER_A,
        scoreB: state.score.PLAYER_B,
        winner: state.winner,
        reason: state.finishReason,
        stateHash,
    };
}
export function createReplayArtifact(initialState, configInput = {}, options = {}) {
    const config = resolveGameConfig(configInput);
    const state = cloneGameState(initialState);
    const initialStateHash = hashGameState(state);
    const checkpointIntervalTicks = options.checkpointIntervalTicks
        ?? DEFAULT_REPLAY_CHECKPOINT_INTERVAL_TICKS;
    const payload = {
        schemaVersion: REPLAY_ARTIFACT_VERSION,
        physicsVersion: PHYSICS_VERSION,
        protocolVersion: PROTOCOL_VERSION,
        metadata: {
            matchId: state.matchId,
            mode: config.rules.mode,
            region: options.metadata?.region ?? null,
            playerAId: options.metadata?.playerAId ?? null,
            playerBId: options.metadata?.playerBId ?? null,
            startedAt: options.metadata?.startedAt ?? null,
            finishedAt: options.metadata?.finishedAt ?? null,
        },
        initial: { seed: state.seed, config, state, stateHash: initialStateHash },
        checkpointIntervalTicks,
        frames: [],
        controlTransitions: [],
        importantEvents: [],
        checkpoints: [{
                sourceIndex: 0,
                tick: state.tick,
                state: cloneGameState(state),
                stateHash: initialStateHash,
            }],
        final: finalFromState(state, initialStateHash),
    };
    return parseReplayArtifact(sealPayload(payload));
}
/** Appends only server-accepted inputs and non-collision events from a canonical StepResult. */
export function appendReplayArtifactFrame(value, result) {
    const artifact = parseReplayArtifact(value);
    if (artifact.final.reason !== null)
        fail("$.frames", "cannot append after the match is finished");
    const state = parseState(result.state, "result.state", resolveLimits());
    const stateHash = expectHash(result.stateHash, "result.stateHash");
    const computedStateHash = hashGameState(state);
    if (stateHash !== computedStateHash) {
        throw new ReplayDivergenceError(state.tick, stateHash, computedStateHash);
    }
    if (state.tick !== artifact.final.tick + 1) {
        fail("result.state.tick", `must be contiguous after tick ${artifact.final.tick}`);
    }
    if (state.matchId !== artifact.metadata.matchId || state.seed !== artifact.initial.seed) {
        fail("result.state", "matchId and seed must match the replay initial state");
    }
    assertStateMatchesConfig(state, artifact.initial.config, "result.state");
    const acceptedInputs = expectArray(result.acceptedInputs, "result.acceptedInputs", DEFAULT_REPLAY_ARTIFACT_LIMITS.maxInputsPerFrame).map((input, index) => parseInput(input, `result.acceptedInputs[${index}]`));
    const importantEvents = expectArray(result.events, "result.events", DEFAULT_REPLAY_ARTIFACT_LIMITS.maxImportantEvents)
        .map((event, index) => parseStepEvent(event, `result.events[${index}]`))
        .filter(isImportantEvent)
        .map(cloneImportantEvent);
    const frames = [
        ...artifact.frames.map(cloneFrame),
        { tick: state.tick, inputs: acceptedInputs.map(cloneInput), stateHash },
    ];
    const checkpoints = artifact.checkpoints.map(cloneCheckpoint);
    const shouldCheckpoint = (state.tick - artifact.initial.state.tick) % artifact.checkpointIntervalTicks === 0
        || state.phase === "finished";
    if (shouldCheckpoint) {
        checkpoints.push({
            sourceIndex: artifact.frames.length + artifact.controlTransitions.length + 1,
            tick: state.tick,
            state: cloneGameState(state),
            stateHash,
        });
    }
    const payload = {
        ...artifactPayload(artifact),
        frames,
        importantEvents: [
            ...artifact.importantEvents.map(cloneImportantEvent),
            ...importantEvents,
        ],
        checkpoints,
        final: finalFromState(state, stateHash),
    };
    return parseReplayArtifact(sealPayload(payload));
}
/** Immutable control append for small replays; long-running matches should use ReplayArtifactBuilder. */
export function appendReplayControlTransition(value, commandValue) {
    const artifact = parseReplayArtifact(value);
    if (artifact.final.reason !== null) {
        fail("$.controlTransitions", "cannot append after the match is finished");
    }
    const current = verifyReplayArtifact(artifact).state;
    const command = parseControlCommand(commandValue, "command");
    const state = applyControlCommand(current, command, artifact.initial.config);
    const stateHash = hashGameState(state);
    const transition = {
        ...command,
        afterFrame: artifact.frames.length,
        tick: state.tick,
        stateHash,
    };
    const sourceIndex = artifact.frames.length + artifact.controlTransitions.length + 1;
    const checkpoints = artifact.checkpoints.map(cloneCheckpoint);
    if (state.phase === "finished") {
        checkpoints.push({
            sourceIndex,
            tick: state.tick,
            state: cloneGameState(state),
            stateHash,
        });
    }
    const payload = {
        ...artifactPayload(artifact),
        controlTransitions: [
            ...artifact.controlTransitions.map(cloneControlTransition),
            transition,
        ],
        checkpoints,
        final: finalFromState(state, stateHash),
    };
    return parseReplayArtifact(sealPayload(payload));
}
/** Marks archive metadata after a deterministic finished-state verification. */
export function finalizeReplayArtifact(value, finishedAt = null) {
    verifyReplayArtifact(value, { requireFinished: true });
    const artifact = parseReplayArtifact(value);
    const payload = {
        ...artifactPayload(artifact),
        metadata: { ...artifact.metadata, finishedAt },
    };
    return parseReplayArtifact(sealPayload(payload));
}
function assertStableEqual(actual, expected, path) {
    if (stableJsonStringify(actual) !== stableJsonStringify(expected)) {
        fail(path, "does not match deterministic playback");
    }
}
/** Re-simulates every frame and verifies inputs, important events, checkpoints, and final evidence. */
export function verifyReplayArtifact(value, options = {}) {
    const artifact = parseReplayArtifact(value, options.limits);
    let state = cloneGameState(artifact.initial.state);
    const replayedEvents = [];
    let checkpointIndex = 1;
    let sourceIndex = 0;
    let controlIndex = 0;
    const verifyCheckpoint = () => {
        const checkpoint = artifact.checkpoints[checkpointIndex];
        if (checkpoint !== undefined && checkpoint.sourceIndex === sourceIndex) {
            assertStableEqual(state, checkpoint.state, `$.checkpoints[${checkpointIndex}].state`);
            checkpointIndex += 1;
        }
    };
    for (let afterFrame = 0; afterFrame <= artifact.frames.length; afterFrame += 1) {
        while (artifact.controlTransitions[controlIndex]?.afterFrame === afterFrame) {
            const transition = artifact.controlTransitions[controlIndex];
            if (transition === undefined)
                break;
            state = applyControlCommand(state, commandFromTransition(transition), artifact.initial.config);
            const stateHash = hashGameState(state);
            if (stateHash !== transition.stateHash) {
                throw new ReplayDivergenceError(transition.tick, transition.stateHash, stateHash);
            }
            sourceIndex += 1;
            verifyCheckpoint();
            controlIndex += 1;
        }
        const frame = artifact.frames[afterFrame];
        if (frame !== undefined) {
            const result = stepGame(state, frame.inputs, artifact.initial.config);
            assertStableEqual(result.acceptedInputs, frame.inputs, `$.frames[${afterFrame}].inputs`);
            if (result.stateHash !== frame.stateHash) {
                throw new ReplayDivergenceError(frame.tick, frame.stateHash, result.stateHash);
            }
            replayedEvents.push(...result.events.filter(isImportantEvent).map(cloneImportantEvent));
            state = result.state;
            sourceIndex += 1;
            verifyCheckpoint();
        }
    }
    assertStableEqual(replayedEvents, artifact.importantEvents, "$.importantEvents");
    if (checkpointIndex !== artifact.checkpoints.length) {
        fail("$.checkpoints", "not all checkpoints were reached during playback");
    }
    const finalStateHash = hashGameState(state);
    if (finalStateHash !== artifact.final.stateHash) {
        throw new ReplayDivergenceError(state.tick, artifact.final.stateHash, finalStateHash);
    }
    const expectedFinal = finalFromState(state, finalStateHash);
    assertStableEqual(expectedFinal, artifact.final, "$.final");
    const finished = state.phase === "finished";
    if (options.requireFinished === true && !finished) {
        fail("$.final.reason", "a finalized replay must end in a finished state");
    }
    return {
        state: cloneGameState(state),
        finalStateHash,
        verifiedFrames: artifact.frames.length,
        verifiedControlTransitions: artifact.controlTransitions.length,
        verifiedSources: sourceIndex,
        verifiedEvents: artifact.importantEvents.length,
        verifiedCheckpoints: artifact.checkpoints.length,
        finished,
    };
}
export function playReplayArtifact(value, options = {}) {
    return verifyReplayArtifact(value, options);
}
/**
 * Single-pass accumulator for live or long-running matches. Per-source appends only
 * validate/copy the new fixed-size record; hashing, strict parsing, and playback run once at seal.
 */
export class ReplayArtifactBuilder {
    #limits;
    #initial;
    #metadata;
    #checkpointIntervalTicks;
    #frames = [];
    #controlTransitions = [];
    #importantEvents = [];
    #checkpoints;
    #requestedFinishedAt;
    #state;
    #stateHash;
    #totalInputs = 0;
    #sourceCount = 0;
    #incrementalSourceValidations = 0;
    #artifactSealOperations = 1;
    #sealed = false;
    constructor(initialState, configInput = {}, options = {}) {
        this.#limits = resolveLimits(options.limits);
        this.#requestedFinishedAt = options.metadata?.finishedAt ?? null;
        const initialMetadata = {
            ...options.metadata,
            finishedAt: initialState.phase === "finished" ? this.#requestedFinishedAt : null,
        };
        const createOptions = options.checkpointIntervalTicks === undefined
            ? { metadata: initialMetadata }
            : { metadata: initialMetadata, checkpointIntervalTicks: options.checkpointIntervalTicks };
        const artifact = parseReplayArtifact(createReplayArtifact(initialState, configInput, createOptions), this.#limits);
        this.#initial = {
            seed: artifact.initial.seed,
            config: resolveGameConfig(artifact.initial.config),
            state: cloneGameState(artifact.initial.state),
            stateHash: artifact.initial.stateHash,
        };
        this.#metadata = { ...artifact.metadata };
        this.#checkpointIntervalTicks = artifact.checkpointIntervalTicks;
        this.#checkpoints = artifact.checkpoints.map(cloneCheckpoint);
        this.#state = cloneGameState(artifact.initial.state);
        this.#stateHash = artifact.initial.stateHash;
    }
    get currentState() {
        return cloneGameState(this.#state);
    }
    get isFinished() {
        return this.#state.phase === "finished";
    }
    get stats() {
        return {
            appendedFrames: this.#frames.length,
            appliedControlTransitions: this.#controlTransitions.length,
            incrementalSourceValidations: this.#incrementalSourceValidations,
            artifactSealOperations: this.#artifactSealOperations,
        };
    }
    #assertOpen() {
        if (this.#sealed)
            fail("builder", "replay artifact builder is already sealed");
    }
    #assertCanAppend(path) {
        this.#assertOpen();
        if (this.#state.phase === "finished")
            fail(path, "cannot append after the match is finished");
    }
    #pushCheckpoint(state, stateHash) {
        if (this.#checkpoints.length >= this.#limits.maxCheckpoints) {
            fail("builder.checkpoints", `exceeds maximum count ${this.#limits.maxCheckpoints}`);
        }
        this.#checkpoints.push({
            sourceIndex: this.#sourceCount,
            tick: state.tick,
            state: cloneGameState(state),
            stateHash,
        });
    }
    appendFrame(result) {
        this.#assertCanAppend("builder.frames");
        if (this.#frames.length >= this.#limits.maxFrames) {
            fail("builder.frames", `exceeds maximum count ${this.#limits.maxFrames}`);
        }
        const state = parseState(result.state, "result.state", this.#limits);
        const stateHash = expectHash(result.stateHash, "result.stateHash");
        const computedStateHash = hashGameState(state);
        if (stateHash !== computedStateHash) {
            throw new ReplayDivergenceError(state.tick, stateHash, computedStateHash);
        }
        if (state.tick !== this.#state.tick + 1) {
            fail("result.state.tick", `must be contiguous after tick ${this.#state.tick}`);
        }
        if (state.matchId !== this.#metadata.matchId || state.seed !== this.#initial.seed) {
            fail("result.state", "matchId and seed must match the replay initial state");
        }
        assertStateMatchesConfig(state, this.#initial.config, "result.state");
        const acceptedInputs = expectArray(result.acceptedInputs, "result.acceptedInputs", this.#limits.maxInputsPerFrame).map((input, index) => parseInput(input, `result.acceptedInputs[${index}]`));
        if (this.#totalInputs + acceptedInputs.length > this.#limits.maxInputs) {
            fail("builder.frames", `exceeds aggregate input count ${this.#limits.maxInputs}`);
        }
        const events = expectArray(result.events, "result.events", this.#limits.maxImportantEvents)
            .map((event, index) => parseStepEvent(event, `result.events[${index}]`))
            .filter(isImportantEvent)
            .map(cloneImportantEvent);
        if (this.#importantEvents.length + events.length > this.#limits.maxImportantEvents) {
            fail("builder.importantEvents", `exceeds maximum count ${this.#limits.maxImportantEvents}`);
        }
        this.#frames.push({
            tick: state.tick,
            inputs: acceptedInputs.map(cloneInput),
            stateHash,
        });
        this.#importantEvents.push(...events);
        this.#totalInputs += acceptedInputs.length;
        this.#sourceCount += 1;
        this.#incrementalSourceValidations += 1;
        this.#state = state;
        this.#stateHash = stateHash;
        if ((state.tick - this.#initial.state.tick) % this.#checkpointIntervalTicks === 0
            || state.phase === "finished") {
            this.#pushCheckpoint(state, stateHash);
        }
        return this;
    }
    applyControl(commandValue) {
        this.#assertCanAppend("builder.controlTransitions");
        if (this.#controlTransitions.length >= this.#limits.maxControlTransitions) {
            fail("builder.controlTransitions", `exceeds maximum count ${this.#limits.maxControlTransitions}`);
        }
        const command = parseControlCommand(commandValue, "command");
        const state = applyControlCommand(this.#state, command, this.#initial.config);
        const stateHash = hashGameState(state);
        this.#controlTransitions.push({
            ...command,
            afterFrame: this.#frames.length,
            tick: state.tick,
            stateHash,
        });
        this.#sourceCount += 1;
        this.#incrementalSourceValidations += 1;
        this.#state = state;
        this.#stateHash = stateHash;
        if (state.phase === "finished")
            this.#pushCheckpoint(state, stateHash);
        return this;
    }
    #seal(finishedAt, requireFinished) {
        this.#assertOpen();
        if (requireFinished && this.#state.phase !== "finished") {
            fail("builder.final", "cannot finalize an unfinished replay");
        }
        this.#sealed = true;
        this.#artifactSealOperations += 1;
        const payload = {
            schemaVersion: REPLAY_ARTIFACT_VERSION,
            physicsVersion: PHYSICS_VERSION,
            protocolVersion: PROTOCOL_VERSION,
            metadata: { ...this.#metadata, finishedAt },
            initial: {
                seed: this.#initial.seed,
                config: resolveGameConfig(this.#initial.config),
                state: cloneGameState(this.#initial.state),
                stateHash: this.#initial.stateHash,
            },
            checkpointIntervalTicks: this.#checkpointIntervalTicks,
            frames: this.#frames.map(cloneFrame),
            controlTransitions: this.#controlTransitions.map(cloneControlTransition),
            importantEvents: this.#importantEvents.map(cloneImportantEvent),
            checkpoints: this.#checkpoints.map(cloneCheckpoint),
            final: finalFromState(this.#state, this.#stateHash),
        };
        const artifact = parseReplayArtifact(sealPayload(payload), this.#limits);
        verifyReplayArtifact(artifact, { limits: this.#limits, requireFinished });
        return artifact;
    }
    build() {
        return this.#seal(this.#requestedFinishedAt, false);
    }
    finalize(finishedAt = this.#requestedFinishedAt) {
        return this.#seal(finishedAt, true);
    }
}
function isStepResultSource(source) {
    return source !== null && typeof source === "object" && "state" in source;
}
/** Consumes a frame/control iterable exactly once and seals/verifies the artifact once. */
export function buildReplayArtifact(initialState, configInput, sources, options = {}) {
    const builder = new ReplayArtifactBuilder(initialState, configInput, options);
    for (const source of sources) {
        if (isStepResultSource(source))
            builder.appendFrame(source);
        else
            builder.applyControl(source);
    }
    return builder.isFinished
        ? builder.finalize(options.metadata?.finishedAt ?? null)
        : builder.build();
}
//# sourceMappingURL=replay-artifact.js.map